"use client";

import { NumberField as NumberFieldPrimitive } from "@base-ui/react/number-field";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";

import { cn } from "@/lib/utils";

const numberFieldInputClassName =
  "w-full min-w-0 flex-1 self-stretch border-0 bg-transparent px-2.5 py-1 text-base transition-colors outline-none placeholder:text-muted-foreground focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-transparent";

const numberFieldStepperClassName =
  "flex min-h-0 flex-1 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-50";

function NumberField({
  className,
  format,
  ...props
}: NumberFieldPrimitive.Root.Props) {
  return (
    <NumberFieldPrimitive.Root
      data-slot="number-field"
      format={format ?? { maximumFractionDigits: 0 }}
      className={cn("w-full", className)}
      {...props}
    />
  );
}

function NumberFieldGroup({
  className,
  invalid,
  ...props
}: NumberFieldPrimitive.Group.Props & {
  invalid?: boolean;
}) {
  return (
    <NumberFieldPrimitive.Group
      data-slot="number-field-group"
      className={cn(

        "flex h-8 w-full min-w-0 overflow-hidden rounded-lg border border-input bg-transparent transition-colors has-[input:focus-visible]:border-[#333] dark:bg-input/30",
        invalid &&
          "border-destructive ring-3 ring-destructive/20 dark:border-destructive/50 dark:ring-destructive/40",
        className,
      )}
      {...props}
    />
  );
}

function NumberFieldInput({
  className,
  ...props
}: NumberFieldPrimitive.Input.Props) {
  return (
    <NumberFieldPrimitive.Input
      data-slot="number-field-input"
      className={cn(numberFieldInputClassName, className)}
      {...props}
    />
  );
}

function NumberFieldIncrement({
  className,
  ...props
}: NumberFieldPrimitive.Increment.Props) {
  return (
    <NumberFieldPrimitive.Increment
      data-slot="number-field-increment"
      className={cn(
        numberFieldStepperClassName,
        "border-b border-input",
        className,
      )}
      {...props}
    >
      <ChevronUpIcon className="size-3 max-h-full" />
    </NumberFieldPrimitive.Increment>
  );
}

function NumberFieldDecrement({
  className,
  ...props
}: NumberFieldPrimitive.Decrement.Props) {
  return (
    <NumberFieldPrimitive.Decrement
      data-slot="number-field-decrement"
      className={cn(numberFieldStepperClassName, className)}
      {...props}
    >
      <ChevronDownIcon className="size-3 max-h-full" />
    </NumberFieldPrimitive.Decrement>
  );
}

function parseNumberFieldValue(
  value: number | string | undefined | null,
  integer = true,
) {
  if (value === "" || value === undefined || value === null) {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const parsed = integer
    ? Number.parseInt(value, 10)
    : Number.parseFloat(value);

  return Number.isFinite(parsed) ? parsed : null;
}

type NumberInputProps = Omit<
  NumberFieldPrimitive.Root.Props,
  "value" | "defaultValue" | "onValueChange"
> & {
  value?: number | string | null;
  onValueChange?: (value: number | null) => void;
  inputClassName?: string;
  placeholder?: string;

  integer?: boolean;
  invalid?: boolean;
  onBlur?: NumberFieldPrimitive.Input.Props["onBlur"];
};

// 步长的小数位数，用来推 integer 与展示精度

function stepFractionDigits(step: number | "any" | undefined): number {
  if (typeof step !== "number" || !Number.isFinite(step)) {
    return 0;
  }

  const text = String(step);
  if (text.includes("e-")) {
    return Number(text.split("e-")[1]) || 0;
  }

  return text.split(".")[1]?.length ?? 0;
}

function NumberInput({
  value,
  onValueChange,
  className,
  inputClassName,
  placeholder,
  disabled,
  id,
  min,
  max,
  step,
  integer,
  format,
  invalid = false,
  onBlur,
  ...props
}: NumberInputProps) {
  const stepDigits = stepFractionDigits(step);
  const isInteger = integer ?? stepDigits === 0;
  const resolvedStep = step ?? (isInteger ? 1 : 0.01);
  const resolvedFormat =
    format ??
    (isInteger
      ? { maximumFractionDigits: 0 }
      : {
          minimumFractionDigits: 0,

          maximumFractionDigits: Math.max(2, stepDigits),
        });

  return (
    <NumberField
      id={id}
      value={parseNumberFieldValue(value, isInteger)}
      onValueChange={onValueChange}
      disabled={disabled}
      min={min}
      max={max}
      step={resolvedStep}
      format={resolvedFormat}
      className={className}
      {...props}
    >
      <NumberFieldGroup invalid={invalid}>
        <NumberFieldInput
          className={inputClassName}
          placeholder={placeholder}
          aria-invalid={invalid || undefined}
          onBlur={onBlur}
        />
        <div className="grid min-h-0 w-8 shrink-0 grid-rows-2 border-l border-input">
          <NumberFieldIncrement />
          <NumberFieldDecrement />
        </div>
      </NumberFieldGroup>
    </NumberField>
  );
}

export {
  NumberField,
  NumberFieldDecrement,
  NumberFieldGroup,
  NumberFieldIncrement,
  NumberFieldInput,
  NumberInput,
};
